import React, { useState } from 'react';
import { useApp } from '../../context/AppContext.tsx';
import {
  Megaphone,
  Save,
  CheckCircle2,
  CheckCircle,
  MessageSquare,
  PhoneCall,
  ShieldCheck,
} from 'lucide-react';
import { buildWaUrl, formatPhoneTel } from '../../lib/contactUtils.ts';

export default function SoftsellingCtaManager() {
  const { settings, updateSettingsOnServer, showToast } = useApp();

  const [softsellingCta, setSoftsellingCta] = useState(
    settings?.softsellingCta || {
      badge: 'STRATEGIC B2B COLLABORATION',
      title: 'Accelerate Your Enterprise’s Sustainability Transition Today',
      subtitle:
        'Schedule a consultation with Greenjobs ITB sustainability advisors and explore customized corporate talent and ESG solutions.',
      benefit1: 'Strategic Proposal in 24 Hours',
      benefit2: 'Certified ESG & Sustainability Specialists',
      benefit3: 'Initial Consultation Zero Cost',
      waBtnText: 'Hubungi Admin WhatsApp',
      phoneBtnText: 'Telepon Kantor Langsung',
      defaultWaMessage:
        'Hello Greenjobs ITB Team, I would like to consult on sustainability programs and corporate partnerships.',
    }
  );

  const [isSaving, setIsSaving] = useState(false);
  const [lastSavedTime, setLastSavedTime] = useState<string | null>(null);

  const handleSave = async () => {
    if (!settings) return;
    setIsSaving(true);
    try {
      await updateSettingsOnServer({
        ...settings,
        softsellingCta,
      });
      const timeStr = new Date().toLocaleTimeString('id-ID', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
      setLastSavedTime(timeStr);
      showToast('Pengaturan Banner Penawaran (CTA) berhasil disimpan ke database!', 'success');
    } catch (err: any) {
      showToast(err.message || 'Gagal menyimpan Banner Penawaran', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const primaryColor = settings?.theme?.primaryColor || '#1B4332';
  const activeWa = settings?.contact?.whatsappNumber || '6281220008890';
  const activePhone = settings?.contact?.phone || '+62 22 2500935';

  return (
    <div className="space-y-6 animate-in fade-in duration-200">
      {/* Header & Save Button */}
      <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-emerald-800 text-xs font-bold mb-2 border border-emerald-200">
            <Megaphone className="w-3.5 h-3.5 text-emerald-600" />
            <span>Bagian dari Grid Tata Letak Beranda</span>
          </div>
          <h2 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight">
            Pengaturan Banner Penawaran & Kolaborasi (CTA)
          </h2>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">
            Atur naskah penawaran, 3 poin manfaat keunggulan, serta tombol aksi WhatsApp dan telepon yang tampil di beranda.
          </p>
        </div>

        <div className="flex flex-col items-end gap-1.5 shrink-0">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-6 py-2.5 rounded-xl text-white font-bold text-xs sm:text-sm shadow-md flex items-center gap-2 transition-all cursor-pointer hover:opacity-90 disabled:opacity-50"
            style={{ backgroundColor: primaryColor }}
          >
            {isSaving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Menyimpan...</span>
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                <span>Simpan Banner Penawaran</span>
              </>
            )}
          </button>

          {lastSavedTime && (
            <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 bg-emerald-50 border border-emerald-300 rounded-lg text-emerald-800 text-[11px] font-bold">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
              <span>Tersimpan ({lastSavedTime})</span>
            </div>
          )}
        </div>
      </div>

      {/* Live Preview of Banner */}
      <div className="space-y-2">
        <span className="text-[11px] font-black uppercase tracking-wider text-slate-600">
          Pratinjau Tampilan Live di Beranda:
        </span>
        <div className="rounded-2xl overflow-hidden shadow-lg p-6 sm:p-8 bg-slate-900 text-white relative">
          <div
            className="absolute top-0 right-0 w-64 h-64 rounded-full blur-3xl opacity-20 pointer-events-none"
            style={{ backgroundColor: primaryColor }}
          />
          <div className="relative z-10 space-y-4">
            <span className="inline-block px-3 py-1 rounded-full text-[10px] font-black tracking-wider uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-400/30">
              {softsellingCta.badge || 'PROPOSAL & ADVISORY'}
            </span>
            <h3 className="text-xl sm:text-2xl font-black tracking-tight leading-tight">
              {softsellingCta.title || 'Judul Penawaran'}
            </h3>
            <p className="text-xs sm:text-sm text-slate-300 max-w-2xl leading-relaxed">
              {softsellingCta.subtitle || 'Deskripsi ajakan bertindak...'}
            </p>

            {/* 3 Benefits */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 pt-2 border-t border-slate-800">
              <div className="flex items-center gap-2 text-xs font-bold text-emerald-300">
                <CheckCircle className="w-4 h-4 shrink-0 text-emerald-400" />
                <span>{softsellingCta.benefit1 || 'Manfaat 1'}</span>
              </div>
              <div className="flex items-center gap-2 text-xs font-bold text-emerald-300">
                <CheckCircle className="w-4 h-4 shrink-0 text-emerald-400" />
                <span>{softsellingCta.benefit2 || 'Manfaat 2'}</span>
              </div>
              <div className="flex items-center gap-2 text-xs font-bold text-emerald-300">
                <CheckCircle className="w-4 h-4 shrink-0 text-emerald-400" />
                <span>{softsellingCta.benefit3 || 'Manfaat 3'}</span>
              </div>
            </div>

            {/* Buttons preview */}
            <div className="flex flex-wrap items-center gap-3 pt-3">
              <span className="px-4 py-2 rounded-xl bg-emerald-600 text-white text-xs font-bold flex items-center gap-2 shadow-xs">
                <MessageSquare className="w-3.5 h-3.5" />
                <span>{softsellingCta.waBtnText || 'Hubungi Admin WhatsApp'}</span>
              </span>
              <span className="px-4 py-2 rounded-xl bg-slate-800 text-slate-200 text-xs font-bold flex items-center gap-2 border border-slate-700">
                <PhoneCall className="w-3.5 h-3.5 text-amber-400" />
                <span>{softsellingCta.phoneBtnText || 'Telepon Kantor'}</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Editor Form */}
      <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-2xs space-y-4">
        <h3 className="text-sm font-extrabold text-slate-900 border-b border-slate-100 pb-2">
          Formulir Kustomisasi Banner Penawaran
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">
              Badge Label Atas
            </label>
            <input
              type="text"
              value={softsellingCta.badge || ''}
              onChange={(e) => setSoftsellingCta({ ...softsellingCta, badge: e.target.value })}
              className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
              placeholder="STRATEGIC B2B COLLABORATION"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">
              Label Tombol WhatsApp
            </label>
            <input
              type="text"
              value={softsellingCta.waBtnText || ''}
              onChange={(e) => setSoftsellingCta({ ...softsellingCta, waBtnText: e.target.value })}
              className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
              placeholder="Hubungi Admin WhatsApp"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-xs font-bold text-slate-700 mb-1">
              Judul Utama Banner Penawaran
            </label>
            <input
              type="text"
              value={softsellingCta.title || ''}
              onChange={(e) => setSoftsellingCta({ ...softsellingCta, title: e.target.value })}
              className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm font-bold text-slate-900 focus:ring-2 focus:ring-emerald-500 focus:outline-none"
              placeholder="Accelerate Your Enterprise’s Sustainability Transition Today"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-xs font-bold text-slate-700 mb-1">
              Sub-judul / Paragraf Penjelasan
            </label>
            <textarea
              rows={2}
              value={softsellingCta.subtitle || ''}
              onChange={(e) => setSoftsellingCta({ ...softsellingCta, subtitle: e.target.value })}
              className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
              placeholder="Jelaskan penawaran atau ajakan konsultasi secara jelas..."
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">
              Poin Manfaat / Keunggulan #1
            </label>
            <input
              type="text"
              value={softsellingCta.benefit1 || ''}
              onChange={(e) => setSoftsellingCta({ ...softsellingCta, benefit1: e.target.value })}
              className="w-full px-3.5 py-2 rounded-xl border border-slate-200 text-xs focus:ring-2 focus:ring-emerald-500"
              placeholder="Strategic Proposal in 24 Hours"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">
              Poin Manfaat / Keunggulan #2
            </label>
            <input
              type="text"
              value={softsellingCta.benefit2 || ''}
              onChange={(e) => setSoftsellingCta({ ...softsellingCta, benefit2: e.target.value })}
              className="w-full px-3.5 py-2 rounded-xl border border-slate-200 text-xs focus:ring-2 focus:ring-emerald-500"
              placeholder="Certified ESG & Sustainability Specialists"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">
              Poin Manfaat / Keunggulan #3
            </label>
            <input
              type="text"
              value={softsellingCta.benefit3 || ''}
              onChange={(e) => setSoftsellingCta({ ...softsellingCta, benefit3: e.target.value })}
              className="w-full px-3.5 py-2 rounded-xl border border-slate-200 text-xs focus:ring-2 focus:ring-emerald-500"
              placeholder="Initial Consultation Zero Cost"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">
              Label Tombol Telepon Kantor
            </label>
            <input
              type="text"
              value={softsellingCta.phoneBtnText || ''}
              onChange={(e) => setSoftsellingCta({ ...softsellingCta, phoneBtnText: e.target.value })}
              className="w-full px-3.5 py-2 rounded-xl border border-slate-200 text-xs focus:ring-2 focus:ring-emerald-500"
              placeholder="Telepon Kantor Langsung"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-xs font-bold text-slate-700 mb-1">
              Pesan Template WhatsApp Otomatis (Saat tombol diklik)
            </label>
            <input
              type="text"
              value={softsellingCta.defaultWaMessage || ''}
              onChange={(e) => setSoftsellingCta({ ...softsellingCta, defaultWaMessage: e.target.value })}
              className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
              placeholder="Hello Greenjobs ITB Team, I would like to consult on sustainability programs..."
            />
          </div>
        </div>
      </div>
    </div>
  );
}
